import type { RangeEntryWrapper } from '../../Common/Components/Selects/RangeCombo/Types'
import { IsVarEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { ArrayUtils } from '../../../NonReact/utils/ArrayUtils'

/**
 * <p>Expands {@link RangeEntryWrapper} selections into plain identifier strings for device or ACD agent
 * membership APIs. Skips excluded entries ({@code included === false}). Numeric ranges expand to each value;
 * non-numeric range display segments are emitted as a single combined token when expansion is not safe.</p>
 *
 * <p>Callers should pass the {@code ranges} argument from {@link RangeCombo}'s {@code onChange} callback
 * (second parameter), not the delimited string, so exclusions and range boundaries stay consistent with the
 * component.</p>
 *
 * @param ranges - Selected range wrappers from {@link RangeCombo}, or {@code null} / empty when cleared.
 * @returns Device numbers or agent numbers to send on {@code DeviceMembers} / {@code ACDAgentMembers}.
 */
export const rangeEntryWrappersToMemberIdentifiers = (ranges: Nullable<RangeEntryWrapper[]>): readonly string[] => {
    if (IsVarEmpty(ranges) || ArrayUtils.isEmpty(ranges)) {
        return []
    }

    return ranges.flatMap((wrapper: RangeEntryWrapper): string[] => {
        if (wrapper.included === false) return []

        if (wrapper.isRange) {
            const { min, max, minDisplayValue, maxDisplayValue } = wrapper.value
            const numericPad: boolean = /^\d+$/.test(minDisplayValue) && /^\d+$/.test(maxDisplayValue)
            const hasExplicitLeadingZeroPadding: boolean =
                /^0\d+$/.test(minDisplayValue) || /^0\d+$/.test(maxDisplayValue)
            const padWidth: number = hasExplicitLeadingZeroPadding
                ? Math.max(minDisplayValue.length, maxDisplayValue.length)
                : 0
            const span: number = max - min + 1

            return Array.from({ length: span }, (_: unknown, index: number): string => {
                const n: number = min + index

                return numericPad && padWidth > 0 ? String(n).padStart(padWidth, '0') : String(n)
            })
        }

        return [String(wrapper.value)]
    })
}
